import { useEffect, useState } from "react";
import api from "../api/axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getToken } from "../auth/auth";
import AlertBox from "../components/AlertBox";
import { useTranslation } from "react-i18next"; // <-- 1. استدعاء الترجمة

const API_BASE = "https://store.wageh.net/blog";

export default function ProductDetails() {
    const { id } = useParams();
    const nav = useNavigate();
    const token = getToken();
    const { t } = useTranslation(); // <-- 2. تعريف الترجمة

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);

    const [qty, setQty] = useState(1);

    useEffect(() => {
        setLoading(true);
        setErr("");

        api.get(`/products/${id}`)
            .then((res) => {
                const payload = res.data.data ?? res.data;
                setProduct(payload.data ?? payload);
            })
            .catch((e) => {
                setErr(e.response?.data?.message || t("product_load_error"));
            })
            .finally(() => setLoading(false));
    }, [id, t]);

    const handleQtyChange = (type) => {
        if (type === "inc" && qty < Number(product.stock)) {
            setQty(qty + 1);
        } else if (type === "dec" && qty > 1) {
            setQty(qty - 1);
        }
    };

    const addToCart = async () => {
        setErr("");
        setMsg("");

        if (!token) {
            nav("/login");
            return;
        }

        setBusy(true);
        try {
            await api.post("/cart/items", { product_id: product.id, qty: qty });
            setMsg(t("added_to_cart_success"));
        } catch (e) {
            setErr(e.response?.data?.message || t("add_to_cart_failed"));
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} />
                <p className="mt-3 text-muted">{t("loading_product_details")}</p>
            </div>
        );
    }

    if (err || !product) {
        return (
            <div className="container py-5 text-center">
                <div className="alert alert-danger mb-4">{err || t("product_not_found")}</div>
                <button onClick={() => nav(-1)} className="btn btn-outline-secondary px-4">
                    <i className="bi bi-arrow-left me-2"></i> {t("back_btn")}
                </button>
            </div>
        );
    }

    const cleanPath = product.image ? product.image.replace(/^\/+/, '').replace(/^storage\//, '').replace(/^uploads\//, '') : "";
    const imgSrc = product.image
        ? (product.image.startsWith("http") ? product.image : `${API_BASE}/uploads/${cleanPath}`)
        : null;

    const inStock = Number(product.stock) > 0;

    return (
        <div className="container-fluid mt-4 mb-5">
            <div className="d-flex justify-content-between mb-3 px-3">
                <button onClick={() => nav(-1)} className="btn btn-sm btn-outline-secondary">
                    <i className="bi bi-arrow-left me-1"></i> {t("go_back")}
                </button>
                <Link to="/cart" className="btn btn-sm btn-outline-primary">
                    <i className="bi bi-cart3 me-1"></i> {t("go_to_cart")}
                </Link>
            </div>

            <div className="mx-3">
                <AlertBox type="success" message={msg} onClose={() => setMsg("")} />
                <AlertBox type="danger" message={err} onClose={() => setErr("")} />
            </div>

            <div className="row g-4 px-3">
                <div className="col-md-5 text-center">
                    <div className="card border-0 shadow-sm p-3 mb-3">
                        {imgSrc ? (
                            <img
                                src={imgSrc}
                                className="img-fluid rounded"
                                alt={product.name}
                                style={{ maxHeight: "400px", objectFit: "contain" }}
                            />
                        ) : (
                            <div className="bg-light d-flex align-items-center justify-content-center rounded" style={{ height: "400px" }}>
                                <span className="text-muted">{t("no_image")}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-md-7">
                    <h1 className="h3 d-inline me-2 fw-bold text-dark">{product.name}</h1>

                    <span className={`badge ${inStock ? "bg-success" : "bg-danger"} align-top me-2`}>
                        {inStock ? t("in_stock") : t("out_of_stock")}
                    </span>

                    <dl className="row small mt-4 mb-4 bg-light p-3 rounded">
                        <dt className="col-sm-3 text-muted">{t("stock_status")}</dt>
                        <dd className="col-sm-9 fw-bold">{t("pieces_available", { count: product.stock })}</dd>

                        <dt className="col-sm-3 text-muted">{t("category_label")}</dt>
                        <dd className="col-sm-9 fw-bold">{product.category?.name || t("general_category")}</dd>
                    </dl>

                    <div className="mb-4">
                        <span className="fw-bold display-6 text-success me-2">${product.price}</span>
                    </div>

                    <div className="mb-4 d-flex align-items-center flex-wrap gap-3" dir="ltr">
                        <div className="input-group input-group-sm" style={{ maxWidth: "130px" }}>
                            <button
                                className="btn btn-primary text-white px-3"
                                type="button"
                                onClick={() => handleQtyChange("dec")}
                                disabled={qty <= 1 || !inStock}
                            >
                                <i className="bi bi-dash-lg"></i>
                            </button>
                            <input
                                type="text"
                                className="form-control text-center fw-bold"
                                value={qty}
                                readOnly
                            />
                            <button
                                className="btn btn-primary text-white px-3"
                                type="button"
                                onClick={() => handleQtyChange("inc")}
                                disabled={qty >= Number(product.stock) || !inStock}
                            >
                                <i className="bi bi-plus-lg"></i>
                            </button>
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary px-4 py-2"
                            onClick={addToCart}
                            disabled={!inStock || busy}
                        >
                            <i className="bi bi-cart-plus mx-2"></i>
                            {busy ? t("adding_to_cart") : t("add_to_cart_btn")}
                        </button>
                    </div>

                    {!token && (
                        <p className="text-danger small mt-2">
                            <i className="bi bi-info-circle me-1"></i> {t("login_required_cart")}
                        </p>
                    )}
                </div>
            </div>

            <div className="row mt-5 px-3">
                <div className="col-md-12">
                    <nav>
                        <div className="nav nav-tabs" id="nav-tab" role="tablist">
                            <button className="nav-link active fw-bold" data-bs-toggle="tab" data-bs-target="#nav-details" type="button" role="tab">
                                {t("product_details_tab")}
                            </button>
                        </div>
                    </nav>
                    <div className="tab-content p-4 border border-top-0 bg-white shadow-sm rounded-bottom" id="nav-tabContent">
                        <div className="tab-pane fade show active" id="nav-details" role="tabpanel">
                            <p className="text-muted lh-lg" style={{ fontSize: "1.1rem" }}>
                                {product.description || t("no_description")}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}